import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Plus, RefreshCw } from 'react-feather';
import TablaServidor from '../tabla/TablaServidor';
import Avatar from './Avatar';
import EstadoBadge from './EstadoBadge';
import PrioridadTag from './PrioridadTag';
import SlaBadge from './SlaBadge';
import { COLUMNAS_TICKETS, TABLA_TICKETS } from './columnasTickets';
import { tiempoRelativo, slaEstado, TICKETS_HEADQUARTER } from '../../features/tickets';

const limpio = (texto) =>
  String(texto || '')
    .replace(/\s+/g, ' ')
    .trim();

// Lo que necesita `slaEstado` (mismo formato que el detalle del ticket).
const comoTicket = (f) => ({
  estado: f.estadoId,
  creado_en: f.creadoUtc,
  vence_sla_en: f.venceUtc,
});

const renderCelda = (col, fila) => {
  switch (col.id) {
    case 'id':
      return `#${fila.id}`;
    case 'estado':
      return <EstadoBadge estadoId={fila.estadoId} />;
    case 'prioridad':
      return <PrioridadTag prioridad={fila.prioridad} />;
    case 'agente':
      return (
        <span className="tickets-agente">
          <Avatar nombre={fila.agente} size="sm" />
          {fila.agente}
        </span>
      );
    case 'ultimoComentario':
      if (!fila.ultimoComentario) return null;
      return (
        <span
          title={
            fila.ultimoAutorTipo === 'agente'
              ? 'Último comentario: de soporte'
              : 'Último comentario: del solicitante, pendiente de respuesta'
          }
        >
          {fila.ultimoAutor}: {Number(fila.ultimoEsPublico) === 0 ? '[Nota interna] ' : ''}
          {limpio(fila.ultimoComentario)}
        </span>
      );
    case 'actualizado':
      return (
        <span className="tickets-row__edad-inner">
          <span className="tickets-row__edad-rel">{tiempoRelativo(fila.actualizadoUtc)}</span>
          <SlaBadge sla={slaEstado(comoTicket(fila))} />
        </span>
      );
    default:
      return undefined;
  }
};

const OBJETO_VACIO_FILTROS = {};
// La bandeja/vista ya impone estado y grupo: los filtros rápidos cubren el resto.
const FILTROS_TICKETS = ['estado', 'prioridad', 'agente', 'tema', 'grupo', 'canal'];

/**
 * Lista de tickets con la tabla estándar de ADMIN. `filtros` trae lo que impone la bandeja o
 * la vista elegida en la barra lateral (estado, agente, grupo, sin asignar).
 */
const TicketsList = ({
  titulo,
  filtros = OBJETO_VACIO_FILTROS,
  refresco = 0,
  onOpen,
  onNew,
  onRefresh,
  panelSuperior = null,
}) => {
  const params = useMemo(() => {
    const p = { empresaId: TICKETS_HEADQUARTER };
    if (filtros.estado) p.estado = String(filtros.estado);
    if (filtros.agente) p.agente = String(filtros.agente);
    if (filtros.grupo) p.grupo = String(filtros.grupo);
    if (filtros.sinAsignar) p.sinAsignar = 'true';
    return p;
  }, [filtros]);

  return (
    <section className="tickets-panel">
      <h1 className="tickets-listhead__title">{titulo}</h1>
      <TablaServidor
        tablaKey={TABLA_TICKETS}
        columnas={COLUMNAS_TICKETS}
        ruta="tickets"
        archivo="tickets"
        nombreEntidad="tickets"
        placeholder="Buscar por nº, asunto o solicitante…"
        params={params}
        refresco={refresco}
        filtrosRapidos={FILTROS_TICKETS}
        panelSuperior={panelSuperior}
        panelInicial
        claveFila={(f) => `${f.empresaId}-${f.id}`}
        tituloTarjeta={(f) => `#${f.id} ${f.asunto || ''}`}
        onFilaClick={(f) => onOpen(f.id)}
        renderCelda={renderCelda}
        barraExtra={
          <>
            <button
              type="button"
              className="tickets-btn tickets-btn--secondary tickets-btn--icon"
              onClick={onRefresh}
              aria-label="Refrescar tickets"
              title="Refrescar"
            >
              <RefreshCw size={16} aria-hidden="true" />
            </button>
            <button type="button" className="tickets-btn tickets-btn--primary" onClick={onNew}>
              <Plus size={16} aria-hidden="true" />
              Nuevo ticket
            </button>
          </>
        }
      />
    </section>
  );
};

TicketsList.propTypes = {
  titulo: PropTypes.string.isRequired,
  filtros: PropTypes.shape({
    estado: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    agente: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    grupo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sinAsignar: PropTypes.bool,
  }),
  refresco: PropTypes.number,
  onOpen: PropTypes.func.isRequired,
  onNew: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  panelSuperior: PropTypes.node,
};

export default TicketsList;
